import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { environment } from 'src/environments/environment';
import { IBarcodeReadPayload, IBarcodeReadResponse, IOcrPayload, IOcrResponse } from '../../models/ocr';
import { CameraService } from 'src/app/shared/services/camera.service';

/**
 * Configuración de calidad de video según el preset
 */
interface IVideoQualityConfig {
	width: number;
	height: number;
	frameRate: number;
}

const VIDEO_QUALITY_PRESETS: Record<'ULTRA' | 'HIGH' | 'MEDIUM' | 'LOW' | 'CUSTOM' | 'BARCODE', IVideoQualityConfig> = {
	ULTRA: { width: 1920, height: 1080, frameRate: 30 },
	HIGH: { width: 1280, height: 720, frameRate: 30 },
	MEDIUM: { width: 854, height: 480, frameRate: 24 },
	LOW: { width: 640, height: 360, frameRate: 15 },
	CUSTOM: { width: 640, height: 480, frameRate: 30 },
	/** Stream propio del paso barcode: pide hasta 4K; el browser degrada si el dispositivo no lo da. */
	BARCODE: { width: 3840, height: 2160, frameRate: 30 },
};

@Injectable({
	providedIn: 'root',
})
export class OcrService extends ApiService {
	private cameraService = inject(CameraService);

	constructor() {
		super();
	}

	/**
	 * Envía la información del OCR al servidor con timeout
	 */
	public sendInfoOcr(body: IOcrPayload): Observable<IOcrResponse> {
		return this.http.post<IOcrResponse>(environment.identity2 + `/ocr`, body, { headers: this.getHeaders() });
	}

	public sendReadBarcode(body: IBarcodeReadPayload): Observable<IBarcodeReadResponse> {
		return this.http.post<IBarcodeReadResponse>(environment.identity2 + `/ocr/read-barcode`, body, {
			headers: this.getHeaders(),
		});
	}

	/**
	 * Constraints de alta calidad para captura de la franja barcode (PDF417).
	 * Stream independiente del OCR (VideoComponent propio): ideal/max >1080p.
	 */
	get barcodeConstraints(): MediaStreamConstraints {
		const qualityConfig = VIDEO_QUALITY_PRESETS.BARCODE;
		return {
			video: {
				facingMode: 'environment',
				width: { ideal: qualityConfig.width, max: qualityConfig.width },
				height: { ideal: qualityConfig.height, max: qualityConfig.height },
				frameRate: { ideal: qualityConfig.frameRate },
				aspectRatio: { ideal: 16 / 9 },
			},
			audio: this.cameraService.shouldIncludeAudio(),
		};
	}

	/**
	 * Obtiene las constraints de media stream para captura de documentos
	 * Características:
	 * - Usa cámara trasera (environment)
	 * - Aspect ratio 16:9 consistente en todos los dispositivos
	 * - Calidad configurable por environment
	 * - Audio habilitado solo si required_recording está activo
	 */
	get constraints(): MediaStreamConstraints {
		const quality = environment.videoQuality || 'HIGH';
		const qualityConfig = VIDEO_QUALITY_PRESETS[quality];

		return {
			video: {
				facingMode: 'environment',
				width: { ideal: qualityConfig.width },
				height: { ideal: qualityConfig.height },
				frameRate: { ideal: qualityConfig.frameRate },
				aspectRatio: { ideal: 16 / 9 },
			},
			audio: this.cameraService.shouldIncludeAudio(),
		};
	}

	/**
	 * Obtiene constraints específicas para streaming preservando la calidad base
	 * Añade frameRate explícito para asegurar consistencia
	 */
	public getStreamingConstraints(): MediaStreamConstraints {
		const baseConstraints = this.constraints;

		return {
			...baseConstraints,
			audio: this.cameraService.shouldIncludeAudio(),
		};
	}
}
